import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { reviewAPI } from '../lib/api';
import { useStore } from '../context/StoreContext';
import { toast } from 'sonner';
import { Star, ArrowLeft, User, Calendar } from 'lucide-react';

export default function ReviewsPage() {
  const { slug } = useParams();
  const { store, selectStore } = useStore();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [slug]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (!store || store.slug !== slug) {
        await selectStore(slug);
      }
      
      const [reviewsRes, statsRes] = await Promise.all([
        reviewAPI.list(),
        reviewAPI.getStats()
      ]);
      
      setReviews(reviewsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Errore caricamento recensioni:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating, size = 'w-4 h-4') => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to={`/s/${slug}`} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <span className="font-semibold">{store?.name} - Recensioni</span>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-12 section-padding">
        <div className="container-main max-w-4xl">
          <h1 className="text-3xl font-bold mb-8">Recensioni Clienti</h1>
          
          {/* Stats */}
          {stats && (
            <Card className="mb-8">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="text-center">
                    <p className="text-5xl font-bold text-primary">{stats.avg_rating}</p>
                    <div className="flex justify-center my-2">
                      {renderStars(Math.round(stats.avg_rating), 'w-5 h-5')}
                    </div>
                    <p className="text-sm text-muted-foreground">{stats.total_reviews} recensioni</p>
                  </div>
                  
                  <div className="flex-1">
                    {[5, 4, 3, 2, 1].map((rating) => {
                      const count = stats.rating_distribution[rating] || 0;
                      const percentage = stats.total_reviews > 0 
                        ? (count / stats.total_reviews) * 100 
                        : 0;
                      
                      return (
                        <div key={rating} className="flex items-center gap-2 mb-1">
                          <span className="text-sm w-3">{rating}</span>
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-yellow-400 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground w-8">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Reviews List */}
          {reviews.length === 0 ? (
            <Card className="p-8 text-center">
              <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Nessuna recensione ancora.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <Card key={review.id} data-testid={`review-${review.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {review.guest_name || 'Cliente verificato'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {review.service_name} con {review.staff_name}
                          </p>
                        </div>
                      </div>
                      {renderStars(review.rating)}
                    </div>
                    
                    {review.comment && (
                      <p className="text-muted-foreground">{review.comment}</p>
                    )}
                    
                    <p className="text-xs text-muted-foreground mt-3">
                      {new Date(review.created_at).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Review Form Component (for embedding)
export function ReviewForm({ appointmentId, onSuccess }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Seleziona una valutazione');
      return;
    }
    
    setSubmitting(true);
    try {
      await reviewAPI.create({
        appointment_id: appointmentId,
        rating,
        comment: comment || null
      });
      toast.success('Grazie per la tua recensione!');
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio recensione');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium mb-2">La tua valutazione</p>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="p-1 transition-transform hover:scale-110"
            >
              <Star
                className={`w-8 h-8 transition-colors ${
                  star <= (hoverRating || rating)
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
      
      <div>
        <p className="text-sm font-medium mb-2">Commento (opzionale)</p>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Racconta la tua esperienza..."
          rows={3}
        />
      </div>
      
      <Button onClick={handleSubmit} disabled={submitting} className="w-full">
        {submitting ? 'Invio in corso...' : 'Invia Recensione'}
      </Button>
    </div>
  );
}
